/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/naming-convention */
import { AppDispatchType, RootStateType } from 'store'
import { callErrorMsg } from 'helpers/errorMsg'
import { getProductById, getWimsUomList } from 'features/Product/services/product'
import { toastSuccess } from 'utils/toast'
import { AxiosResponse } from 'axios'
import {
  getPackageStructure,
  getPackageStructureDetail,
  deletePackageStructure,
  savePackageStructure,
  SavePackageStructureRequestType,
  getPackageStructureWims,
  deletePackageStructureWims,
  PutPackageStructureSellingTypePayloadType,
  putPackageStructureSellingTypeById,
  CreateUpdatePackageStructurePayloadType,
  postPackageStructureWims,
  putPackageStructureWims,
  PackageStructureWimsItemType,
  getProductDetailById,
} from '../../services/packageStructure'
import {
  setIsLoading,
  setSelectedProduct,
  setParamsPackageStructure,
  setPackageStructureList,
  setPackageStructureDetail,
  setPackageStructureOnFetchSucceed,
  setSummaryInformation,
  setModalDelete,
  resetModalDelete,
  setModalAddEdit,
  resetModalAddEdit,
  setBarcodeNumberOfBaseType,
  setBaseUomTo,
  setIsInnerOrOuterTypeExist,
  TypeOfPackageStructureType,
  setTriggerReloadFormOuter,
} from './slice'

type ParamsPackageStructureType = {
  pageIndex: number
  pageSize: number
  productId: number
}

const getParamsForPackageStructure = (params: Omit<ParamsPackageStructureType, 'productId'>) => ({
  'pagination.page_size': params.pageSize || 20,
  'pagination.page_index': params.pageIndex || 1,
})

type ActFetchPackageStructureParamsType = {
  productId: number
} & Partial<Omit<ParamsPackageStructureType, 'productId'>>

export const actFetchPackageStructure =
  (params: ActFetchPackageStructureParamsType) => async (dispatch: AppDispatchType) => {
    const { productId, pageSize, pageIndex } = params
    try {
      dispatch(setIsLoading(true))
      const { data } = await getPackageStructure(
        productId,
        getParamsForPackageStructure({ pageSize: pageSize || 20, pageIndex: pageIndex || 1 }),
      )
      dispatch(setParamsPackageStructure(params))
      dispatch(
        setPackageStructureList({
          data: data.data,
          pagination: data.pagination,
        }),
      )
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const actRemovePackageStructure =
  (id: number) => async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    const { params } = getState().packageStructure
    try {
      dispatch(setIsLoading(true))
      await deletePackageStructure(id)
      dispatch(actFetchPackageStructure(params))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const actCreateOrUpdatePackageStructure =
  (formData: SavePackageStructureRequestType, callbackFunction: () => void) =>
  async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    const { params } = getState().packageStructure
    try {
      dispatch(setIsLoading(true))
      const { data } = await savePackageStructure(formData)
      dispatch(actFetchPackageStructure(params))
      toastSuccess(data.message)
      if (callbackFunction) callbackFunction()
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const actFetchProductById = (productId: number) => async (dispatch: AppDispatchType) => {
  try {
    const { data } = await getProductById(productId)
    dispatch(setSelectedProduct(data))
  } catch (error) {
    callErrorMsg(error)
  }
}

export const actFetchPackageStructureWims =
  () => async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    dispatch(setIsLoading(true))
    try {
      const { productId, queryParams } = getState().packageStructure

      const { data } = await getPackageStructureWims(productId, {
        'pagination.page_size': queryParams.page_size || 20,
        'pagination.page_index': queryParams.page_index || 1,
      })

      const baseItemData = data.data.find(
        (packageItem) => packageItem.type.toLowerCase() === 'base',
      )

      let isInnerExist = false
      let isOuterExist = false
      const newListData = data.data.map((item) => ({ ...item }))

      newListData.forEach((item) => {
        if (item.type.toLowerCase() === 'inner') {
          isInnerExist = true
        } else if (item.type.toLowerCase() === 'outer') {
          isOuterExist = true
        }
      })

      if (queryParams.page_index === 1 && (isInnerExist || isOuterExist)) {
        dispatch(setIsInnerOrOuterTypeExist(true))
      } else if (queryParams.page_index === 1 && !isInnerExist && !isOuterExist) {
        dispatch(setIsInnerOrOuterTypeExist(false))
      }

      const defaultItemPackageStructure = {
        volume: 0,
        net_weight: 0,
        range_qty_max: 0,
        product_id: productId,
        purchase_currency: 'IDR',
        length: 0,
        gross_weight: 0,
        to_uom_id: baseItemData?.to_uom_id || '',
        use_range_qty: false,
        range_qty_min: 0,
        barcode: '',
        created_at: '',
        updated_at: '',
        from_uom_description: '',
        to_uom_description: baseItemData?.to_uom_description || '',
        type: '',
        id: 0,
        qty: 0,
        purchase_price: 0,
        barcode_source_level: 'SELF',
        from_uom_id: '',
        width: 0,
        height: 0,
        selling_type: '',
      }

      if (!isInnerExist && isOuterExist) {
        const indexOfOuter = newListData.findIndex((item) => item.type.toLowerCase() === 'outer')
        const defaultInnerData = {
          ...defaultItemPackageStructure,
          type: 'INNER',
          id: -1,
        } as PackageStructureWimsItemType
        const indexToBeInserted = indexOfOuter || 0
        newListData.splice(indexToBeInserted, 0, defaultInnerData)
      } else if (isInnerExist && !isOuterExist) {
        const indexOfInner = newListData.findIndex((item) => item.type.toLowerCase() === 'inner')
        const defaultOuterData = {
          ...defaultItemPackageStructure,
          type: 'OUTER',
          id: -2,
        } as PackageStructureWimsItemType
        newListData.splice(indexOfInner + 1, 0, defaultOuterData)
      }

      const newDataPackageStructure = {
        data: newListData,
        pagination: data.pagination,
      }

      dispatch(setPackageStructureOnFetchSucceed(newDataPackageStructure))

      if (baseItemData) {
        dispatch(setBarcodeNumberOfBaseType(baseItemData.barcode))
        dispatch(
          setBaseUomTo({
            id: baseItemData.to_uom_id,
            description: baseItemData.to_uom_description,
            name: '',
          }),
        )
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const actFetchProductDetail =
  (id: number) => async (dispatch: AppDispatchType, _getState: () => RootStateType) => {
    try {
      const {
        data: { data },
      } = await getProductDetailById(id)

      const imageProduct = `${process.env.REACT_APP_PLATFORM_IMAGE_BASE_URL}/${data.product_image_urls[0].url_small}`
      const summaryData = {
        product_image: imageProduct,
        product_name: data.product_name,
        product_sku_no: data.product_sku_no,
        product_categories: data.product_categories,
        uom: data.base_uom,
      }
      dispatch(setSummaryInformation(summaryData))
    } catch (error) {
      callErrorMsg(error)
    }
  }

export const actDeletePackageStructureById =
  () => async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    try {
      dispatch(
        setModalDelete({
          isDisabledBtnSubmit: true,
        }),
      )
      const { modalDelete } = getState().packageStructure
      const listPromises = modalDelete.id
        .filter((id) => ![-1, -2].includes(id))
        .map((id) => deletePackageStructureWims(id))
      await Promise.all(listPromises)
      toastSuccess('Package structure berhasil dihapus')

      dispatch(actFetchPackageStructureWims())
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(resetModalDelete())
    }
  }

export const actUpdatePackageStructureSellingType =
  (id: number, sellingType: PutPackageStructureSellingTypePayloadType['selling_type']) =>
  async (dispatch: AppDispatchType) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await putPackageStructureSellingTypeById(id, { selling_type: sellingType })
      toastSuccess(data.message)
      dispatch(actFetchPackageStructureWims())
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

export const actFetchUomList = (name?: string) => async (dispatch: AppDispatchType) => {
  try {
    const { data } = await getWimsUomList({
      'pagination.page_index': 1,
      'pagination.page_size': 60,
      'filter.active': true,
      'filter.groups': 'ITEM',
      'filter.name': name || '',
    })

    const listData = data.data.map((item) => ({
      id: item.id,
      description: item.description,
      name: `${item.description} (${item.id})`,
    }))
    dispatch(
      setModalAddEdit({
        uomList: listData,
      }),
    )
  } catch (error) {
    callErrorMsg(error)
  }
}

export const actFetchPackageStructureDetail =
  (id: number, type: string, callbackFunction?: () => void) =>
  async (dispatch: AppDispatchType) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await getPackageStructureDetail(id)
      dispatch(
        setPackageStructureDetail({
          [type]: data.data,
        }),
      )
      if (callbackFunction) callbackFunction()
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  }

const isHitApiAdd = (
  modalType: string,
  isInnerOuterFormEdit: boolean,
  type: TypeOfPackageStructureType,
) =>
  modalType === 'add' ||
  (modalType === 'edit' && !isInnerOuterFormEdit && ['inner', 'outer'].includes(type))

const actCheckOuterDataAndResaveAgain =
  (toUom: string) => async (_dispatch: AppDispatchType, getState: () => RootStateType) => {
    const { packageStructureWimsList } = getState().packageStructure
    const outerItem = packageStructureWimsList.find(
      (item) => item.type.toLowerCase() === 'outer' && item.id !== -2,
    )

    if (!outerItem) {
      return
    }
    const dataToSubmit = {
      product_id: outerItem.product_id,
      type: outerItem.type as CreateUpdatePackageStructurePayloadType['type'],
      from_uom_id: outerItem.from_uom_id,
      to_uom_id: toUom,
      use_range_qty: outerItem.use_range_qty,
      qty: outerItem.qty,
      range_qty_min: outerItem.range_qty_min,
      range_qty_max: outerItem.range_qty_max,
      barcode: outerItem.barcode,
      barcode_source_level: outerItem.barcode_source_level,
      purchase_price: outerItem.purchase_price,
      purchase_currency: outerItem.purchase_currency,
      width: outerItem.width,
      height: outerItem.height,
      length: outerItem.length,
      volume: outerItem.volume,
      net_weight: outerItem.net_weight,
      gross_weight: outerItem.gross_weight,
    }

    try {
      ;(await putPackageStructureWims(outerItem.id, dataToSubmit)) as unknown as AxiosResponse<{
        data: PackageStructureWimsItemType
      }>
    } catch (error) {
      callErrorMsg(error)
    }
  }

export const actCreateUpdatePackageStructure =
  (
    type: TypeOfPackageStructureType,
    data: CreateUpdatePackageStructurePayloadType,
    callbackFunction?: () => void,
  ) =>
  async (dispatch: AppDispatchType, getState: () => RootStateType) => {
    const { modalAddEdit } = getState().packageStructure
    const { id, modalType, isInnerFormEdit, isOuterFormEdit } = modalAddEdit

    dispatch(
      setModalAddEdit({
        isDisabledBtnSubmit: {
          [type]: true,
        },
      }),
    )
    const idOfPackageStructure = id[type]
    const isInnerOuterFormEdit =
      (type === 'inner' && isInnerFormEdit) || (type === 'outer' && isOuterFormEdit)
    const shouldHitAddApi = isHitApiAdd(modalType, isInnerOuterFormEdit, type)

    try {
      await (shouldHitAddApi
        ? postPackageStructureWims(data)
        : putPackageStructureWims(idOfPackageStructure, data))
      toastSuccess(`Package structure ${type} berhasil disimpan`)

      if (type === 'inner') {
        dispatch(actCheckOuterDataAndResaveAgain(data.from_uom_id))
      }

      if (['inner', 'outer'].includes(type)) {
        dispatch(
          setModalAddEdit({
            isDisabledBtnSubmit: {
              [type]: false,
            },
          }),
        )
        dispatch(actFetchPackageStructureWims())
        if (callbackFunction) callbackFunction()
      } else {
        dispatch(resetModalAddEdit())
        dispatch(actFetchPackageStructureWims())
      }
    } catch (error) {
      callErrorMsg(error)
    }
  }
